import React, { useState, useEffect } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import LeadDetailModal from '@/components/LeadDetailModal';
import {
    Phone, Clock, User, CheckCircle, Sun, Sunset, Moon, Calendar,
    RefreshCw, Bell, TrendingUp, DollarSign, ExternalLink,
    AlertTriangle, XCircle, Filter,
} from 'lucide-react';

const SLA_STYLES = {
    completed: { label: 'Called', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', icon: CheckCircle },
    completed_late: { label: 'Called Late', color: 'bg-amber-500/10 text-amber-600 border-amber-500/30', icon: Clock },
    breach: { label: 'SLA Breach', color: 'bg-red-500/10 text-red-600 border-red-500/30', icon: XCircle },
    pending: { label: 'Due Now', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30', icon: Clock },
    upcoming: { label: 'Upcoming', color: 'bg-gray-500/10 text-gray-500 border-gray-500/30', icon: Clock },
};

function FollowupCard({ item, onComplete, onCall, onOpenCard }) {
    const isLead = item.entity_type === 'lead';
    const typeLabel = isLead ? 'Lead' : (item.reminder_type === 'upgrade' ? 'Upgrade' : item.reminder_type === 'redeposit' ? 'Redeposit' : 'Student');
    const typeColor = isLead ? 'bg-blue-500' : (item.reminder_type === 'upgrade' ? 'bg-emerald-500' : item.reminder_type === 'redeposit' ? 'bg-purple-500' : 'bg-gray-500');
    const sla = SLA_STYLES[item.followup_sla] || SLA_STYLES.upcoming;
    const SlaIcon = sla.icon;
    
    return (
        <Card 
            className={`hover:shadow-md transition-shadow cursor-pointer hover:border-primary/50 ${item.followup_sla === 'breach' ? 'border-red-500/50 bg-red-500/5' : ''}`}
            onClick={() => onOpenCard(item)}
            data-testid={`followup-card-${item.id}`}
        >
            <CardContent className="p-4">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className={typeColor}>{typeLabel}</Badge>
                            {item.reminder_time && (
                                <span className="text-sm text-muted-foreground flex items-center gap-1">
                                    <Clock className="h-3 w-3" />{item.reminder_time}
                                </span>
                            )}
                            {!item.reminder_time && item.follow_up_date && (
                                <span className="text-sm text-muted-foreground flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {new Date(item.follow_up_date).toLocaleTimeString('en-AE', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            )}
                            <Badge variant="outline" className={`text-[10px] ${sla.color}`}>
                                <SlaIcon className="h-3 w-3 mr-0.5" />{sla.label}
                            </Badge>
                        </div>
                        <p className="font-semibold flex items-center gap-2">
                            {item.full_name}
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />{item.phone}
                        </p>
                        {item.assigned_to_name && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <User className="h-3 w-3" />{item.assigned_to_name}
                            </p>
                        )}
                        {item.reminder_note && (
                            <p className="text-xs text-muted-foreground mt-1 italic">"{item.reminder_note}"</p>
                        )}
                    </div>
                    <div className="flex flex-col gap-1">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onCall(item); }} data-testid={`call-btn-${item.id}`}>
                            <Phone className="h-3 w-3 mr-1" />Call
                        </Button>
                        {item.followup_sla !== 'completed' && (
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onComplete(item); }} data-testid={`complete-btn-${item.id}`}>
                                <CheckCircle className="h-3 w-3 mr-1" />Done
                            </Button>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function TimeSlotColumn({ title, icon, items, onComplete, onCall, onOpenCard }) {
    const Icon = icon;
    const list = (Array.isArray(items) ? items : []).filter(Boolean);
    const breaches = list.filter(i => i.followup_sla === 'breach').length;
    return (
        <div className="flex-1 min-w-[300px]">
            <div className="flex items-center gap-2 mb-4 p-3 bg-muted/50 rounded-lg">
                <Icon className="h-5 w-5 text-primary" />
                <span className="font-semibold">{title}</span>
                <Badge variant="secondary">{list.length}</Badge>
                {breaches > 0 && <Badge className="bg-red-500 text-white ml-auto">{breaches} breach</Badge>}
            </div>
            <ScrollArea className="h-[calc(100vh-340px)]">
                <div className="space-y-3 pr-2">
                    {list.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No follow-ups</p>
                    ) : (
                        list.map(item => (
                            <FollowupCard key={item.id} item={item} onComplete={onComplete} onCall={onCall} onOpenCard={onOpenCard} />
                        ))
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}

function FollowupsPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedLead, setSelectedLead] = useState(null);
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [filterAgent, setFilterAgent] = useState('all');

    const isManager = ['team_leader', 'super_admin', 'admin', 'coo', 'sales_manager'].includes(user?.role);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(function() { loadFollowups(); }, [filterAgent]);

    async function loadFollowups() {
        setLoading(true);
        try {
            const params = filterAgent !== 'all' ? `?agent_id=${filterAgent}` : '';
            const res = await apiClient.get(`/followups/today${params}`);
            setData(res.data);
        } catch {
            toast.error('Failed to load follow-ups');
        }
        setLoading(false);
    }

    async function handleComplete(item) {
        try {
            const endpoint = item.entity_type === 'lead' 
                ? '/leads/' + item.id + '/reminder/complete'
                : '/students/' + item.id + '/reminder/complete';
            await apiClient.post(endpoint);
            toast.success('Follow-up completed');
            loadFollowups();
        } catch {
            toast.error('Failed to complete');
        }
    }

    function handleCall(item) { window.open('tel:' + item.phone, '_blank'); }

    function handleOpenCard(item) {
        if (item.entity_type === 'lead') {
            setSelectedLead(item);
            setShowLeadModal(true);
        } else {
            toast.info('Student details coming soon');
        }
    }

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    const followups = data?.followups || { morning: [], afternoon: [], evening: [], unscheduled: [] };
    const agents = data?.agents || [];
    // Defensive flatten — backend should always return arrays, but a single
    // null/undefined slot would make the whole page crash on the filter
    // below. This is the only reason Followups would appear broken.
    const allFollowups = ['morning', 'afternoon', 'evening', 'unscheduled']
        .flatMap(slot => Array.isArray(followups[slot]) ? followups[slot] : [])
        .filter(Boolean);
    const completedCount = allFollowups.filter(i => i?.followup_sla === 'completed' || i?.followup_sla === 'completed_late').length;

    return (
        <div className="space-y-5" data-testid="followups-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Bell className="h-7 w-7 text-primary" />
                        Today's Follow-ups
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        {data?.date} — {data?.total_followups || 0} scheduled
                        {data?.sla_breaches > 0 && <span className="text-red-500 font-medium ml-2">{data.sla_breaches} SLA breaches</span>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isManager && agents.length > 0 && (
                        <Select value={filterAgent} onValueChange={setFilterAgent}>
                            <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="followup-agent-filter">
                                <Filter className="h-3.5 w-3.5 mr-1" />
                                <SelectValue placeholder="All Agents" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Agents</SelectItem>
                                {agents.map(a => (
                                    <SelectItem key={a.id} value={a.id}>
                                        {a.name} {a.team ? `(${a.team})` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Button onClick={loadFollowups} variant="outline" size="sm" data-testid="refresh-followups">
                        <RefreshCw className="h-4 w-4 mr-1" />Refresh
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{data?.total_followups || 0}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-500">{data?.leads_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Leads</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-500">{data?.students_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Students</p>
                </CardContent></Card>
                <Card className={data?.sla_breaches > 0 ? 'border-red-500/50' : ''}>
                    <CardContent className="p-4 text-center">
                        <p className={`text-2xl font-bold ${data?.sla_breaches > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>{data?.sla_breaches || 0}</p>
                        <p className="text-xs text-muted-foreground">SLA Breaches</p>
                    </CardContent>
                </Card>
                <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{completedCount}</p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                </CardContent></Card>
            </div>

            <div className="flex gap-4 overflow-x-auto pb-4">
                <TimeSlotColumn title="Morning (Before 12 PM)" icon={Sun} items={followups.morning} onComplete={handleComplete} onCall={handleCall} onOpenCard={handleOpenCard} />
                <TimeSlotColumn title="Afternoon (12 - 5 PM)" icon={Sunset} items={followups.afternoon} onComplete={handleComplete} onCall={handleCall} onOpenCard={handleOpenCard} />
                <TimeSlotColumn title="Evening (After 5 PM)" icon={Moon} items={followups.evening} onComplete={handleComplete} onCall={handleCall} onOpenCard={handleOpenCard} />
                <TimeSlotColumn title="Unscheduled" icon={Calendar} items={followups.unscheduled} onComplete={handleComplete} onCall={handleCall} onOpenCard={handleOpenCard} />
            </div>

            {showLeadModal && selectedLead && (
                <LeadDetailModal
                    lead={selectedLead}
                    open={showLeadModal}
                    onClose={() => { setShowLeadModal(false); setSelectedLead(null); }}
                    onUpdate={loadFollowups}
                />
            )}
        </div>
    );
}

export default FollowupsPage;
